/** @typedef {import('../../types/auth').MerchantUser} MerchantUser */
/** @typedef {import('../../types/auth').Merchant} Merchant */

import { refreshMerchantUserDetails } from '@basqet-dashboard/common/api';
import { findMerchantByName } from '@basqet-dashboard/common/api/merchants';
import { APP_NAME } from '@basqet-dashboard/common/config';
import { useUserContext } from '@basqet-dashboard/common/context/user';
import { useSessionStorage } from '@basqet-dashboard/common/hooks';
import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';

import { handleQueryError } from '../../utils';

/** @returns {{ isLoading: boolean, profile: MerchantUser, refetch: Function, userHasVerifiedEmail: boolean }} */
const useFetchMerchantProfile = () => {
  const REFETCH_INTERVAL = 10000;
  const [profile, setProfile] = useState([]);
  const { setUser } = useUserContext();
  const [, setStore] = useSessionStorage(APP_NAME, '');

  const { isLoading, data, isError, error, refetch } = useQuery(
    'merchantProfile',
    refreshMerchantUserDetails,
    {
      refetchOnMount: 'always',
      refetchOnWindowFocus: true,
      refetchInterval: REFETCH_INTERVAL,
      staleTime: 0
    }
  );

  if (isError) {
    handleQueryError(error, 'merchant profile');
  }

  useEffect(() => {
    if (data && data.data) {
      const merchantUser = data.data.merchantUser;
      const serialized = JSON.stringify(merchantUser);

      setProfile((prev) => (JSON.stringify(prev) === serialized ? prev : merchantUser));
      setUser((prev) => (JSON.stringify(prev) === serialized ? prev : merchantUser));
      setStore((prevStore) => {
        if (JSON.stringify(prevStore.merchantUser) === serialized) return prevStore;
        return { ...prevStore, merchantUser };
      });
    } else {
      setProfile({});
    }
  }, [data, setUser, setStore]);

  const userHasVerifiedEmail = Boolean(profile.emailVerified);

  return { isLoading, profile, refetch, userHasVerifiedEmail };
};

/**
 * @param {string} search
 * @returns {{ isLoading: boolean, merchants: Merchant[], refetch: Function }}
 */
export const useFetchMerchants = (search) => {
  const [merchants, setMerchants] = useState([]);
  const { isLoading, data, isError, error, refetch } = useQuery(
    ['merchants', search],
    () => findMerchantByName(search),
    { enabled: Boolean(search) }
  );

  if (isError) {
    handleQueryError(error, 'merchant search');
  }

  useEffect(() => {
    if (data && data.data) {
      setMerchants(data.data);
    } else {
      setMerchants([]);
    }
  }, [data]);

  return { isLoading, merchants, refetch };
};

export { useFetchMerchantProfile };
